import { Box } from '@mui/material';
import { isAfter } from 'date-fns';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { ToWords } from 'to-words';

import { selectCurrentLanguageCode } from '!core/features';
import { TrlFormSectionSeparator } from '»components/atoms';
import {
  TrlDatePicker,
  TrlFormDropdown,
  TrlSearchInput,
  TrlTextField,
} from '»components/atoms/Inputs';
import { TrlFormRadioGroup } from '»components/molecules/TrlFormRadioGroup';
import {
  isAureaActiveClient,
  isBfacmActiveClient,
  isHemeraActiveClient,
  isStinvestActiveClient,
} from '»utils/clientsUtils';
import { useFieldPropertyVerification } from '»utils/customHooks';
import {
  normalizeFieldWithoutSpaces,
  normalizeIbanWithAngolanPrefix,
} from '»utils/form';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import { onboardingMainFormFieldsSelector } from '../../redux';

export const NaturalPersonFields = () => {
  const mainFormFields = useSelector(onboardingMainFormFieldsSelector);
  const languageCode = useSelector(selectCurrentLanguageCode);

  const { verifyFieldProperty } = useFieldPropertyVerification(mainFormFields);

  const { values, setFieldValue } = useFormikContext();

  const currencies = {
    EUR: {
      name: TrlFormattedMessagePlain({ id: 'TRLT_UI-EURO' }),
      plural: TrlFormattedMessagePlain({ id: 'TRLT_UI-EUROS' }),
      symbol: '€',
      fractionalUnit: {
        name: TrlFormattedMessagePlain({ id: 'TRLT_UI-CENT' }),
        plural: TrlFormattedMessagePlain({ id: 'TRLT_UI-CENTS' }),
        symbol: '',
      },
    },
    AOA: {
      name: TrlFormattedMessagePlain({ id: 'TRLT_UI-KWANZA' }),
      plural: TrlFormattedMessagePlain({ id: 'TRLT_UI-KWANZAS' }),
      symbol: 'Kz',
      fractionalUnit: {
        name: TrlFormattedMessagePlain({ id: 'TRLT_UI-CENT' }),
        plural: TrlFormattedMessagePlain({ id: 'TRLT_UI-CENTS' }),
        symbol: '',
      },
    },
    USD: {
      name: TrlFormattedMessagePlain({ id: 'TRLT_UI-DOLLAR' }),
      plural: TrlFormattedMessagePlain({ id: 'TRLT_UI-DOLLARS' }),
      symbol: '$',
      fractionalUnit: {
        name: TrlFormattedMessagePlain({ id: 'TRLT_UI-CENT' }),
        plural: TrlFormattedMessagePlain({ id: 'TRLT_UI-CENTS' }),
        symbol: '',
      },
    },
  };

  const tw = new ToWords({
    localeCode: languageCode,
  });

  const convertValueToWords = ({ value, currencyCode }) => {
    const currencyOptions = currencies[currencyCode];
    const normalizedValue = String(value ?? '').trim();
    const isValidIntegerValue = /^\d+$/.test(normalizedValue);

    if (!normalizedValue || !currencyOptions || !isValidIntegerValue) return '';

    try {
      return tw.convert(normalizedValue, {
        currency: true,
        currencyOptions,
        doNotAddOnly: true,
      });
    } catch {
      return '';
    }
  };

  const yesLabel = TrlFormattedMessagePlain({
    id: 'TRLT_UI-YES',
  });

  const noLabel = TrlFormattedMessagePlain({
    id: 'TRLT_UI-NO',
  });

  const validityExpiredLabel = TrlFormattedMessagePlain({
    id: 'TRLT_UI-VALIDITY_EXPIRED',
  });

  const todaysDate = new Date();
  todaysDate.setHours(0, 0, 0, 0);

  const getFieldOptions = (currentFieldName) => {
    const currentField = mainFormFields.find(
      (field) => field.name === currentFieldName,
    );

    return currentField?.options ?? [];
  };

  const fieldsApiInitialValue = mainFormFields.reduce((acc, field) => {
    acc[field.name] = field.apiInitialValue;
    return acc;
  }, {});

  const hasInitialValue = (fieldName) =>
    Array.isArray(fieldsApiInitialValue[fieldName])
      ? fieldsApiInitialValue[fieldName].length !== 0
      : fieldsApiInitialValue[fieldName] !== '';

  const nifInitialValue = verifyFieldProperty({
    fieldName: 'nif',
    fieldProperty: 'apiInitialValue',
  });

  const isFieldHidden = {
    otherIdType: verifyFieldProperty({
      fieldName: 'otherIdType',
      fieldProperty: 'isHidden',
    }),
    idNumber: verifyFieldProperty({
      fieldName: 'idNumber',
      fieldProperty: 'isHidden',
    }),
    idEntity: verifyFieldProperty({
      fieldName: 'idEntity',
      fieldProperty: 'isHidden',
    }),
    idExpDate: verifyFieldProperty({
      fieldName: 'idExpDate',
      fieldProperty: 'isHidden',
    }),
    idIssueLocal: verifyFieldProperty({
      fieldName: 'idIssueLocal',
      fieldProperty: 'isHidden',
    }),
    idIssueDate: verifyFieldProperty({
      fieldName: 'idIssueDate',
      fieldProperty: 'isHidden',
    }),
    regime: verifyFieldProperty({
      fieldName: 'regime',
      fieldProperty: 'isHidden',
    }),
    regimeFree: verifyFieldProperty({
      fieldName: 'regimeFree',
      fieldProperty: 'isHidden',
    }),
    employer: verifyFieldProperty({
      fieldName: 'employer',
      fieldProperty: 'isHidden',
    }),
    admissionDate: verifyFieldProperty({
      fieldName: 'admissionDate',
      fieldProperty: 'isHidden',
    }),
    profContractTerm: verifyFieldProperty({
      fieldName: 'profContractTerm',
      fieldProperty: 'isHidden',
    }),
    role: verifyFieldProperty({ fieldName: 'role', fieldProperty: 'isHidden' }),
    cpActivity: verifyFieldProperty({
      fieldName: 'cpActivity',
      fieldProperty: 'isHidden',
    }),
    profCommercialName: verifyFieldProperty({
      fieldName: 'profCommercialName',
      fieldProperty: 'isHidden',
    }),
    profNif: verifyFieldProperty({
      fieldName: 'profNif',
      fieldProperty: 'isHidden',
    }),
    indivEntrepActivity: verifyFieldProperty({
      fieldName: 'indivEntrepActivity',
      fieldProperty: 'isHidden',
    }),
    headOffice: verifyFieldProperty({
      fieldName: 'headOffice',
      fieldProperty: 'isHidden',
    }),
    employeeTypeDescr: verifyFieldProperty({
      fieldName: 'employeeTypeDescr',
      fieldProperty: 'isHidden',
    }),
    profStatusOthers: verifyFieldProperty({
      fieldName: 'profStatusOthers',
      fieldProperty: 'isHidden',
    }),
    ppePosition: verifyFieldProperty({
      fieldName: 'ppePosition',
      fieldProperty: 'isHidden',
    }),
    ppeEntity: verifyFieldProperty({
      fieldName: 'ppeEntity',
      fieldProperty: 'isHidden',
    }),
    ppePositionHolder: verifyFieldProperty({
      fieldName: 'ppePositionHolder',
      fieldProperty: 'isHidden',
    }),
    ppeOthersName: verifyFieldProperty({
      fieldName: 'ppeOthersName',
      fieldProperty: 'isHidden',
    }),
    ppePositionRelation: verifyFieldProperty({
      fieldName: 'ppePositionRelation',
      fieldProperty: 'isHidden',
    }),
    ppeInstitution: verifyFieldProperty({
      fieldName: 'ppeInstitution',
      fieldProperty: 'isHidden',
    }),
    ppeAppointDate: verifyFieldProperty({
      fieldName: 'ppeAppointDate',
      fieldProperty: 'isHidden',
    }),
    ppeExonDate: verifyFieldProperty({
      fieldName: 'ppeExonDate',
      fieldProperty: 'isHidden',
    }),
    ppeKinshipDeg: verifyFieldProperty({
      fieldName: 'ppeKinshipDeg',
      fieldProperty: 'isHidden',
    }),
    ppeLeadershipMember: verifyFieldProperty({
      fieldName: 'ppeLeadershipMember',
      fieldProperty: 'isHidden',
    }),
    ppeLeadershipMemberRole: verifyFieldProperty({
      fieldName: 'ppeLeadershipMemberRole',
      fieldProperty: 'isHidden',
    }),
    ppeReligiousOrgOfficer: verifyFieldProperty({
      fieldName: 'ppeReligiousOrgOfficer',
      fieldProperty: 'isHidden',
    }),
    ppeReligiousOrgOfficerRole: verifyFieldProperty({
      fieldName: 'ppeReligiousOrgOfficerRole',
      fieldProperty: 'isHidden',
    }),
    incomeSourceOthers: verifyFieldProperty({
      fieldName: 'incomeSourceOthers',
      fieldProperty: 'isHidden',
    }),
    incomeSourceDescr: verifyFieldProperty({
      fieldName: 'incomeSourceDescr',
      fieldProperty: 'isHidden',
    }),
    relationAssetsOthers: verifyFieldProperty({
      fieldName: 'relationAssetsOthers',
      fieldProperty: 'isHidden',
    }),
    usTaxId: verifyFieldProperty({
      fieldName: 'usTaxId',
      fieldProperty: 'isHidden',
    }),
    ordTransEmail: verifyFieldProperty({
      fieldName: 'ordTransEmail',
      fieldProperty: 'isHidden',
    }),
    ordTransEmail2: verifyFieldProperty({
      fieldName: 'ordTransEmail2',
      fieldProperty: 'isHidden',
    }),
  };

  return (
    <>
      <TrlFormSectionSeparator
        title={TrlFormattedMessagePlain({ id: 'TRLT_UI-IDENTIFICATION' })}
      />
      <Box className="flexWrap">
        <TrlTextField
          name="name"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-FULL_NAME' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-FULL_NAME' })}
          id="name"
          autoComplete="name"
          isLocked={hasInitialValue('name')}
          isHidden={verifyFieldProperty({
            fieldName: 'name',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'name',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlFormDropdown
          name="idType"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ID_TYPE' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-ID_TYPE' })}
          isLocked={hasInitialValue('idType')}
          options={getFieldOptions('idType')}
          isHidden={verifyFieldProperty({
            fieldName: 'idType',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'idType',
            fieldProperty: 'isRequired',
          })}
          onChangeValue={() => {
            if (!isFieldHidden.otherIdType)
              setFieldValue('otherIdType', '', false);
            if (!isFieldHidden.idNumber) setFieldValue('idNumber', '', false);
            if (!isFieldHidden.idExpDate) setFieldValue('idExpDate', '', false);
            if (!isFieldHidden.idEntity) setFieldValue('idEntity', '', false);
            if (!isFieldHidden.idIssueLocal)
              setFieldValue('idIssueLocal', '', false);
            if (!isFieldHidden.idIssueDate)
              setFieldValue('idIssueDate', '', false);
          }}
        />
        {values.idType === 'OUTRO' && (
          <TrlTextField
            name="otherIdType"
            type="text"
            label={TrlFormattedMessagePlain({ id: 'TRLT_UI-OTHER' })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-OTHER',
            })}
            id="otherIdType"
            isLocked={hasInitialValue('otherIdType')}
            isHidden={verifyFieldProperty({
              fieldName: 'otherIdType',
              fieldProperty: 'isHidden',
            })}
            isRequired={verifyFieldProperty({
              fieldName: 'otherIdType',
              fieldProperty: 'isRequired',
            })}
          />
        )}
        <TrlTextField
          name="idNumber"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ID_NUMBER' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-ID_NUMBER' })}
          id="idNumber"
          isLocked={hasInitialValue('idNumber')}
          isHidden={isFieldHidden.idNumber}
          isRequired={verifyFieldProperty({
            fieldName: 'idNumber',
            fieldProperty: 'isRequired',
          })}
          inputProps={{
            maxLength: 20,
          }}
        />

        <TrlTextField
          name="idEntity"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ISSUING_ENTITY_DI' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-ISSUING_ENTITY_DI',
          })}
          id="idEntity"
          isLocked={hasInitialValue('idEntity')}
          isHidden={isFieldHidden.idEntity}
          isRequired={verifyFieldProperty({
            fieldName: 'idEntity',
            fieldProperty: 'isRequired',
          })}
          inputProps={{
            maxLength: 40,
          }}
        />
        <TrlDatePicker
          name="idExpDate"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-EXPIRATION_DATE' })}
          value={values.idExpDate}
          disablePast
          isLocked={hasInitialValue('idExpDate')}
          dateFormatType="date"
          isHidden={isFieldHidden.idExpDate}
          isRequired={verifyFieldProperty({
            fieldName: 'idExpDate',
            fieldProperty: 'isRequired',
          })}
          warningText={
            values.idExpDate &&
            (isAfter(values.idExpDate, todaysDate) ? '' : validityExpiredLabel)
          }
        />
        <TrlTextField
          name="idIssueLocal"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ISSUE_LOCAL' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-ISSUE_LOCAL',
          })}
          id="idIssueLocal"
          isLocked={hasInitialValue('idIssueLocal')}
          isHidden={isFieldHidden.idIssueLocal}
          isRequired={verifyFieldProperty({
            fieldName: 'idIssueLocal',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlDatePicker
          name="idIssueDate"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ISSUE_DATE' })}
          value={values.idIssueDate}
          disableFuture
          isLocked={hasInitialValue('idIssueDate')}
          dateFormatType="date"
          isHidden={isFieldHidden.idIssueDate}
          isRequired={verifyFieldProperty({
            fieldName: 'idIssueDate',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="nif"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-TAX_ID' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-TAX_ID' })}
          id="nif"
          isLocked={
            isStinvestActiveClient &&
            values.idType === 'BIDENTICIVIL' &&
            nifInitialValue === values.idNumber
              ? false
              : hasInitialValue('nif')
          }
          isHidden={verifyFieldProperty({
            fieldName: 'nif',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'nif',
            fieldProperty: 'isRequired',
          })}
        />
        {isStinvestActiveClient ? (
          <TrlSearchInput
            name="placeOfBirth"
            label={TrlFormattedMessagePlain({ id: 'TRLT_UI-PLACE_OF_BIRTH' })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-PLACE_OF_BIRTH',
            })}
            id="placeOfBirth"
            options={getFieldOptions('placeOfBirth')}
            isLocked={hasInitialValue('placeOfBirth')}
            isHidden={verifyFieldProperty({
              fieldName: 'placeOfBirth',
              fieldProperty: 'isHidden',
            })}
            isRequired={verifyFieldProperty({
              fieldName: 'placeOfBirth',
              fieldProperty: 'isRequired',
            })}
          />
        ) : (
          <TrlTextField
            name="placeOfBirth"
            type="text"
            label={TrlFormattedMessagePlain({ id: 'TRLT_UI-PLACE_OF_BIRTH' })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-PLACE_OF_BIRTH',
            })}
            id="placeOfBirth"
            isLocked={hasInitialValue('placeOfBirth')}
            isHidden={verifyFieldProperty({
              fieldName: 'placeOfBirth',
              fieldProperty: 'isHidden',
            })}
            isRequired={verifyFieldProperty({
              fieldName: 'placeOfBirth',
              fieldProperty: 'isRequired',
            })}
          />
        )}
        {isStinvestActiveClient ? (
          <TrlSearchInput
            name="nationality"
            label={TrlFormattedMessagePlain({ id: 'TRLT_UI-NATIONALITY' })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-NATIONALITY',
            })}
            id="nationality"
            options={getFieldOptions('nationality')}
            isLocked={hasInitialValue('nationality')}
            isHidden={verifyFieldProperty({
              fieldName: 'nationality',
              fieldProperty: 'isHidden',
            })}
            isRequired={verifyFieldProperty({
              fieldName: 'nationality',
              fieldProperty: 'isRequired',
            })}
          />
        ) : (
          <TrlTextField
            name="nationality"
            type="text"
            label={TrlFormattedMessagePlain({
              id: 'TRLT_UI-NATIONALITY',
            })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-NATIONALITY',
            })}
            id="nationality"
            isLocked={hasInitialValue('nationality')}
            isHidden={verifyFieldProperty({
              fieldName: 'nationality',
              fieldProperty: 'isHidden',
            })}
            isRequired={verifyFieldProperty({
              fieldName: 'nationality',
              fieldProperty: 'isRequired',
            })}
          />
        )}
        {isStinvestActiveClient ? (
          <TrlSearchInput
            name="otherNationalities"
            label={TrlFormattedMessagePlain({
              id: 'TRLT_UI-OTHER_NATIONALITIES',
            })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-OTHER_NATIONALITIES',
            })}
            id="otherNationalities"
            options={getFieldOptions('otherNationalities')}
            isLocked={hasInitialValue('otherNationalities')}
            isHidden={verifyFieldProperty({
              fieldName: 'otherNationalities',
              fieldProperty: 'isHidden',
            })}
            isRequired={verifyFieldProperty({
              fieldName: 'otherNationalities',
              fieldProperty: 'isRequired',
            })}
          />
        ) : (
          <TrlTextField
            name="otherNationalities"
            type="text"
            label={TrlFormattedMessagePlain({
              id: 'TRLT_UI-OTHER_NATIONALITIES',
            })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-OTHER_NATIONALITIES',
            })}
            id="otherNationalities"
            isLocked={hasInitialValue('otherNationalities')}
            isHidden={verifyFieldProperty({
              fieldName: 'otherNationalities',
              fieldProperty: 'isHidden',
            })}
            isRequired={verifyFieldProperty({
              fieldName: 'otherNationalities',
              fieldProperty: 'isRequired',
            })}
          />
        )}
        <TrlFormDropdown
          name="educQualifications"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-EDUCATIONAL_BACKGROUND',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-EDUCATIONAL_BACKGROUND',
          })}
          isLocked={hasInitialValue('educQualifications')}
          options={getFieldOptions('educQualifications')}
          isHidden={verifyFieldProperty({
            fieldName: 'educQualifications',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'educQualifications',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="iban"
          type="text"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-IBAN',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-IBAN',
          })}
          id="iban"
          isLocked={hasInitialValue('iban')}
          isHidden={verifyFieldProperty({
            fieldName: 'iban',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'iban',
            fieldProperty: 'isRequired',
          })}
          additionalFunc={normalizeIbanWithAngolanPrefix({
            fieldName: 'iban',
            setFieldValue,
          })}
        />
        <TrlTextField
          name="ibanUsd"
          type="text"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-IBAN_USD',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-IBAN_USD',
          })}
          id="ibanUsd"
          isLocked={hasInitialValue('ibanUsd')}
          isHidden={verifyFieldProperty({
            fieldName: 'ibanUsd',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'ibanUsd',
            fieldProperty: 'isRequired',
          })}
          additionalFunc={normalizeFieldWithoutSpaces({
            fieldName: 'ibanUsd',
            setFieldValue,
          })}
        />
        <TrlFormRadioGroup
          name="gender"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-GENDER',
          })}
          options={[
            {
              label: TrlFormattedMessagePlain({
                id: 'TRLT_UI-FEMALE',
              }),
              id: 'F',
            },
            {
              label: TrlFormattedMessagePlain({
                id: 'TRLT_UI-MALE',
              }),
              id: 'M',
            },
          ]}
          isLocked={hasInitialValue('gender')}
          isHidden={verifyFieldProperty({
            fieldName: 'gender',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'gender',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlDatePicker
          name="birthDate"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-BIRTH_DATE' })}
          value={values.birthDate}
          disableFuture
          isLocked={hasInitialValue('birthDate')}
          dateFormatType="date"
          isHidden={verifyFieldProperty({
            fieldName: 'birthDate',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'birthDate',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="fatherName"
          type="text"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-FATHER_NAME',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-FATHER_NAME',
          })}
          id="fatherName"
          isLocked={hasInitialValue('fatherName')}
          isHidden={verifyFieldProperty({
            fieldName: 'fatherName',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'fatherName',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="motherName"
          type="text"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-MOTHER_NAME',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-MOTHER_NAME',
          })}
          id="motherName"
          isLocked={hasInitialValue('motherName')}
          isHidden={verifyFieldProperty({
            fieldName: 'motherName',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'motherName',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlFormDropdown
          name="maritalStatus"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-MARITAL_STATUS',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-MARITAL_STATUS',
          })}
          isLocked={hasInitialValue('maritalStatus')}
          options={getFieldOptions('maritalStatus')}
          isHidden={verifyFieldProperty({
            fieldName: 'maritalStatus',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'maritalStatus',
            fieldProperty: 'isRequired',
          })}
          onChangeValue={(event) => {
            if (
              (isStinvestActiveClient || event.target.value === 'ECV_SOLT') &&
              !isFieldHidden.regime
            )
              setFieldValue('regime', '', false);

            if (event.target.value !== 'ECV_CAS' && !isFieldHidden.regimeFree)
              setFieldValue('regimeFree', '', false);
          }}
        />
        {(isStinvestActiveClient
          ? true
          : values.maritalStatus !== 'ECV_SOLT') && (
          <TrlFormDropdown
            name="regime"
            label={TrlFormattedMessagePlain({
              id: 'TRLT_UI-REGIME',
            })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-REGIME',
            })}
            isLocked={hasInitialValue('regime')}
            options={getFieldOptions('regime')}
            isHidden={isFieldHidden.regime}
            isRequired={
              verifyFieldProperty({
                fieldName: 'regime',
                fieldProperty: 'isRequired',
              }) && isStinvestActiveClient
                ? values.maritalStatus === 'CAS'
                : true
            }
          />
        )}
        {values.maritalStatus === 'ECV_CAS' && (
          <TrlTextField
            name="regimeFree"
            type="text"
            label={TrlFormattedMessagePlain({
              id: 'TRLT_UI-REGIME',
            })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-REGIME',
            })}
            id="regimeFree"
            isLocked={hasInitialValue('regimeFree')}
            isHidden={isFieldHidden.regimeFree}
            isRequired={verifyFieldProperty({
              fieldName: 'regimeFree',
              fieldProperty: 'isRequired',
            })}
          />
        )}
      </Box>

      <TrlFormSectionSeparator
        title={TrlFormattedMessagePlain({ id: 'TRLT_UI-ADDRESS_CONTACTS' })}
      />
      <Box className="flexWrap">
        <TrlTextField
          name="address"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ADDRESS' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-ADDRESS' })}
          id="address"
          autoComplete="street-address"
          isLocked={isStinvestActiveClient ? false : hasInitialValue('address')}
          isHidden={verifyFieldProperty({
            fieldName: 'address',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'address',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="town"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-CITY' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-CITY' })}
          id="town"
          isLocked={isStinvestActiveClient ? false : hasInitialValue('town')}
          isHidden={verifyFieldProperty({
            fieldName: 'town',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'town',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="province"
          type="text"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-PROVINCE' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-PROVINCE' })}
          id="province"
          isLocked={hasInitialValue('province')}
          isHidden={verifyFieldProperty({
            fieldName: 'province',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'province',
            fieldProperty: 'isRequired',
          })}
        />
        {isStinvestActiveClient ? (
          <TrlSearchInput
            name="country"
            label={TrlFormattedMessagePlain({ id: 'TRLT_UI-COUNTRY' })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-COUNTRY',
            })}
            id="country"
            options={getFieldOptions('country')}
            isLocked={hasInitialValue('country')}
            isHidden={verifyFieldProperty({
              fieldName: 'country',
              fieldProperty: 'isHidden',
            })}
            isRequired={verifyFieldProperty({
              fieldName: 'country',
              fieldProperty: 'isRequired',
            })}
          />
        ) : (
          <TrlTextField
            name="country"
            type="text"
            autoComplete="country"
            label={TrlFormattedMessagePlain({ id: 'TRLT_UI-COUNTRY' })}
            placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-COUNTRY' })}
            id="country"
            isLocked={hasInitialValue('country')}
            isHidden={verifyFieldProperty({
              fieldName: 'country',
              fieldProperty: 'isHidden',
            })}
            isRequired={verifyFieldProperty({
              fieldName: 'country',
              fieldProperty: 'isRequired',
            })}
          />
        )}
        <TrlTextField
          name="postCode"
          type="text"
          autoComplete="postal-code"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-POST_CODE' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-POST_CODE' })}
          id="postCode"
          isLocked={hasInitialValue('postCode')}
          isHidden={verifyFieldProperty({
            fieldName: 'postCode',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'postCode',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="telephoneNum"
          type="tel"
          autoComplete="tel"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-PHONE' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-PHONE' })}
          id="telephoneNum"
          isLocked={hasInitialValue('telephoneNum')}
          isHidden={verifyFieldProperty({
            fieldName: 'telephoneNum',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'telephoneNum',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="phoneNumIndicator"
          type="text"
          autoComplete="tel-country-code"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-PHONE_NUM_INDICATOR',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-PHONE_NUM_INDICATOR',
          })}
          id="phoneNumIndicator"
          isLocked={hasInitialValue('phoneNumIndicator')}
          isHidden={verifyFieldProperty({
            fieldName: 'phoneNumIndicator',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'phoneNumIndicator',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="phoneNum"
          type="tel"
          autoComplete="tel"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-MOBILE_PHONE' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-MOBILE_PHONE' })}
          id="phoneNum"
          isLocked={hasInitialValue('phoneNum')}
          isHidden={verifyFieldProperty({
            fieldName: 'phoneNum',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'phoneNum',
            fieldProperty: 'isRequired',
          })}
          extraInfo={
            isAureaActiveClient
              ? TrlFormattedMessagePlain({
                  id: 'TRLT_UI-MOBILE_HINT',
                })
              : ''
          }
        />
        <TrlTextField
          name="phoneNum2Indicator"
          type="text"
          autoComplete="tel-country-code"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-ALTERNATIVE_PHONE_NUM_INDICATOR',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-ALTERNATIVE_PHONE_NUM_INDICATOR',
          })}
          id="phoneNum2Indicator"
          isLocked={hasInitialValue('phoneNum2Indicator')}
          isHidden={verifyFieldProperty({
            fieldName: 'phoneNum2Indicator',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'phoneNum2Indicator',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="phoneNum2"
          type="tel"
          autoComplete="tel"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-ALTERNATIVE_MOBILE_PHONE',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-ALTERNATIVE_MOBILE_PHONE',
          })}
          id="phoneNum2"
          isLocked={hasInitialValue('phoneNum2')}
          isHidden={verifyFieldProperty({
            fieldName: 'phoneNum2',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'phoneNum2',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlTextField
          name="email1"
          type="email"
          autoComplete="email"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-EMAIL' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-EMAIL' })}
          id="email1"
          isLocked={hasInitialValue('email1')}
          isHidden={verifyFieldProperty({
            fieldName: 'email1',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'email1',
            fieldProperty: 'isRequired',
          })}
          extraInfo={
            isAureaActiveClient
              ? TrlFormattedMessagePlain({
                  id: 'TRLT_UI-EMAIL_HINT',
                })
              : ''
          }
        />
        <TrlTextField
          name="email2"
          type="email"
          autoComplete="email"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ALTERNATE_EMAIL' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-ALTERNATE_EMAIL',
          })}
          id="email2"
          isLocked={hasInitialValue('email2')}
          isHidden={verifyFieldProperty({
            fieldName: 'email2',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'email2',
            fieldProperty: 'isRequired',
          })}
          extraInfo={
            isAureaActiveClient
              ? TrlFormattedMessagePlain({
                  id: 'TRLT_UI-EMAIL_HINT',
                })
              : ''
          }
        />
        <TrlTextField
          name="signatureNum"
          type="number"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-SIGNATURE_NUM' })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-SIGNATURE_NUM',
          })}
          id="signatureNum"
          isLocked={hasInitialValue('signatureNum')}
          isHidden={verifyFieldProperty({
            fieldName: 'signatureNum',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'signatureNum',
            fieldProperty: 'isRequired',
          })}
          inputProps={{
            maxLength: 2,
          }}
        />
      </Box>

      <TrlFormSectionSeparator
        title={TrlFormattedMessagePlain({ id: 'TRLT_UI-PROFESSIONAL_DATA' })}
      />
      <Box className="flexWrap">
        <TrlFormDropdown
          name="employeeType"
          label={TrlFormattedMessagePlain({ id: 'TRLT_UI-WORKER_TYPE' })}
          placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-WORKER_TYPE' })}
          isLocked={hasInitialValue('employeeType')}
          isMultipleOption={!isHemeraActiveClient && !isStinvestActiveClient}
          options={getFieldOptions('employeeType')}
          isHidden={verifyFieldProperty({
            fieldName: 'employeeType',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'employeeType',
            fieldProperty: 'isRequired',
          })}
          onChangeValue={(event) => {
            if (!event.target.value.includes('TTB_CO')) {
              if (!isFieldHidden.employer) setFieldValue('employer', '', false);
              if (!isFieldHidden.admissionDate)
                setFieldValue('admissionDate', '', false);
              if (!isFieldHidden.profContractTerm)
                setFieldValue('profContractTerm', '', false);
              if (!isFieldHidden.role) setFieldValue('role', '', false);
            }

            if (
              !event.target.value.includes('TTB_CP') &&
              !isFieldHidden.cpActivity
            )
              setFieldValue('cpActivity', '', false);

            if (!event.target.value.includes('TTB_EMP')) {
              if (!isFieldHidden.profCommercialName)
                setFieldValue('profCommercialName', '', false);
              if (!isFieldHidden.profNif) setFieldValue('profNif', '', false);
              if (!isFieldHidden.indivEntrepActivity)
                setFieldValue('indivEntrepActivity', '', false);
              if (!isFieldHidden.headOffice)
                setFieldValue('headOffice', '', false);
            }

            if (
              !event.target.value.includes('TTB_OUT') &&
              !isFieldHidden.employeeTypeDescr
            )
              setFieldValue('employeeTypeDescr', '', false);
          }}
        />
        {isStinvestActiveClient ? (
          <TrlSearchInput
            name="job"
            label={TrlFormattedMessagePlain({ id: 'TRLT_UI-JOB' })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-JOB',
            })}
            id="job"
            options={getFieldOptions('job')}
            isLocked={hasInitialValue('job')}
            isHidden={verifyFieldProperty({
              fieldName: 'job',
              fieldProperty: 'isHidden',
            })}
            isRequired={
              verifyFieldProperty({
                fieldName: 'job',
                fieldProperty: 'isRequired',
              }) &&
              (values.employeeType?.includes('TTB_CO') ||
                values.employeeType?.includes('TTB_CP'))
            }
          />
        ) : (
          <TrlTextField
            name="job"
            type="text"
            label={TrlFormattedMessagePlain({ id: 'TRLT_UI-JOB' })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-JOB',
            })}
            id="job"
            isLocked={hasInitialValue('job')}
            isHidden={verifyFieldProperty({
              fieldName: 'job',
              fieldProperty: 'isHidden',
            })}
            isRequired={
              verifyFieldProperty({
                fieldName: 'job',
                fieldProperty: 'isRequired',
              }) &&
              (values.employeeType?.includes('TTB_CO') ||
                values.employeeType?.includes('TTB_CP'))
            }
          />
        )}
        {values.employeeType?.includes('TTB_OUT') && (
          <TrlTextField
            name="employeeTypeDescr"
            type="text"
            label={TrlFormattedMessagePlain({
              id: 'TRLT_UI-OTHER_FUNCTION',
            })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-OTHER_FUNCTION',
            })}
            id="employeeTypeDescr"
            isLocked={hasInitialValue('employeeTypeDescr')}
            isHidden={isFieldHidden.employeeTypeDescr}
            isRequired={verifyFieldProperty({
              fieldName: 'employeeTypeDescr',
              fieldProperty: 'isRequired',
            })}
          />
        )}
        {values.employeeType?.includes('TTB_CO') && (
          <>
            <TrlTextField
              name="employer"
              type="text"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-EMPLOYER_ENTITY',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-EMPLOYER_ENTITY',
              })}
              id="employer"
              isLocked={!isAureaActiveClient && hasInitialValue('employer')}
              isHidden={isFieldHidden.employer}
              isRequired={verifyFieldProperty({
                fieldName: 'employer',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlDatePicker
              name="admissionDate"
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-ADMISSION_DATE' })}
              value={values.admissionDate}
              disableFuture
              isLocked={hasInitialValue('admissionDate')}
              dateFormatType="date"
              isHidden={isFieldHidden.admissionDate}
              isRequired={verifyFieldProperty({
                fieldName: 'admissionDate',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="profContractTerm"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-PROFESSIONAL_BOND',
              })}
              options={[
                {
                  label: TrlFormattedMessagePlain({
                    id: 'TRLT_UI-EFFECTIVE',
                  }),
                  id: 'EFETIVO',
                },
                {
                  label: TrlFormattedMessagePlain({
                    id: 'TRLT_UI-TERM',
                  }),
                  id: 'PRAZO',
                },
              ]}
              isLocked={hasInitialValue('profContractTerm')}
              isHidden={isFieldHidden.profContractTerm}
              isRequired={verifyFieldProperty({
                fieldName: 'profContractTerm',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlTextField
              name="role"
              type="text"
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-POSITION' })}
              placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-POSITION' })}
              id="role"
              isLocked={hasInitialValue('role')}
              isHidden={isFieldHidden.role}
              isRequired={verifyFieldProperty({
                fieldName: 'role',
                fieldProperty: 'isRequired',
              })}
            />
          </>
        )}
        {values.employeeType?.includes('TTB_CP') && (
          <TrlTextField
            name="cpActivity"
            type="text"
            label={TrlFormattedMessagePlain({
              id: 'TRLT_UI-SELF_EMPLOYMENT_ACTIVITY',
            })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-SELF_EMPLOYMENT_ACTIVITY',
            })}
            id="cpActivity"
            isLocked={hasInitialValue('cpActivity')}
            isHidden={isFieldHidden.cpActivity}
            isRequired={verifyFieldProperty({
              fieldName: 'cpActivity',
              fieldProperty: 'isRequired',
            })}
          />
        )}
        {values.employeeType?.includes('TTB_EMP') && (
          <>
            <TrlTextField
              name="profCommercialName"
              type="text"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-COMMERCIAL_NAME',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-COMMERCIAL_NAME',
              })}
              id="profCommercialName"
              isLocked={hasInitialValue('profCommercialName')}
              isHidden={isFieldHidden.profCommercialName}
              isRequired={verifyFieldProperty({
                fieldName: 'profCommercialName',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlTextField
              name="profNif"
              type="text"
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-TAX_ID' })}
              placeholder={TrlFormattedMessagePlain({ id: 'TRLT_UI-TAX_ID' })}
              id="profNif"
              isLocked={hasInitialValue('profNif')}
              isHidden={isFieldHidden.profNif}
              isRequired={verifyFieldProperty({
                fieldName: 'profNif',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlTextField
              name="indivEntrepActivity"
              type="text"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-SOLO_ENTREPRENEUR_ACTIVITY',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-SOLO_ENTREPRENEUR_ACTIVITY',
              })}
              id="indivEntrepActivity"
              isLocked={hasInitialValue('indivEntrepActivity')}
              isHidden={isFieldHidden.indivEntrepActivity}
              isRequired={verifyFieldProperty({
                fieldName: 'indivEntrepActivity',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlTextField
              name="headOffice"
              type="text"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-REGISTERED_OFFICE',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-REGISTERED_OFFICE',
              })}
              id="headOffice"
              isLocked={hasInitialValue('headOffice')}
              isHidden={isFieldHidden.headOffice}
              isRequired={verifyFieldProperty({
                fieldName: 'headOffice',
                fieldProperty: 'isRequired',
              })}
            />
          </>
        )}
        <TrlTextField
          name="profCountries"
          type="text"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-COUNTRIES_OF_OPERATION',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-COUNTRIES_OF_OPERATION',
          })}
          id="profCountries"
          isLocked={hasInitialValue('profCountries')}
          isHidden={verifyFieldProperty({
            fieldName: 'profCountries',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'profCountries',
            fieldProperty: 'isRequired',
          })}
        />
        <TrlFormDropdown
          name="profStatus"
          label={TrlFormattedMessagePlain({
            id: 'TRLT_UI-PROFESSIONAL_STATUS',
          })}
          placeholder={TrlFormattedMessagePlain({
            id: 'TRLT_UI-PROFESSIONAL_STATUS',
          })}
          isLocked={hasInitialValue('profStatus')}
          isMultipleOption
          options={getFieldOptions('profStatus')}
          isHidden={verifyFieldProperty({
            fieldName: 'profStatus',
            fieldProperty: 'isHidden',
          })}
          isRequired={verifyFieldProperty({
            fieldName: 'profStatus',
            fieldProperty: 'isRequired',
          })}
          onChangeValue={(event) => {
            if (
              !event.target.value.includes('EPF_OUT') &&
              !isFieldHidden.profStatusOthers
            )
              setFieldValue('profStatusOthers', '', false);
          }}
        />
        {values.profStatus?.includes('EPF_OUT') && (
          <TrlTextField
            name="profStatusOthers"
            type="text"
            label={TrlFormattedMessagePlain({
              id: 'TRLT_UI-OTHERS_DESCR',
            })}
            placeholder={TrlFormattedMessagePlain({
              id: 'TRLT_UI-OTHERS_DESCR',
            })}
            id="profStatusOthers"
            isLocked={hasInitialValue('profStatusOthers')}
            isHidden={isFieldHidden.profStatusOthers}
            isRequired={verifyFieldProperty({
              fieldName: 'profStatusOthers',
              fieldProperty: 'isRequired',
            })}
          />
        )}
      </Box>

      {!isHemeraActiveClient && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-POLITICALLY_EXPOSED_PERSON',
            })}
            infoText={
              isAureaActiveClient
                ? TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_HINT',
                  })
                : ''
            }
          />
          <Box className="flexWrap">
            <TrlFormRadioGroup
              name="ppe"
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-PPE' })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('ppe')}
              isHidden={verifyFieldProperty({
                fieldName: 'ppe',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'ppe',
                fieldProperty: 'isRequired',
              })}
              additionalFunc={(event) => {
                if (event.target.value === 'N') {
                  if (!isFieldHidden.ppePosition)
                    setFieldValue('ppePosition', '', false);
                  if (!isFieldHidden.ppePositionHolder)
                    setFieldValue('ppePositionHolder', '', false);
                  if (!isFieldHidden.ppeOthersName)
                    setFieldValue('ppeOthersName', '', false);
                  if (!isFieldHidden.ppeInstitution)
                    setFieldValue('ppeInstitution', '', false);
                  if (!isFieldHidden.ppeAppointDate)
                    setFieldValue('ppeAppointDate', '', false);
                  if (!isFieldHidden.ppeExonDate)
                    setFieldValue('ppeExonDate', '', false);
                }
              }}
            />
            {values.ppe === 'Y' && (
              <>
                <TrlTextField
                  name="ppePosition"
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_POSITION',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_POSITION',
                  })}
                  id="ppePosition"
                  isLocked={hasInitialValue('ppePosition')}
                  isHidden={isFieldHidden.ppePosition}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ppePosition',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name="ppeEntity"
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_ENTITY',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_ENTITY',
                  })}
                  id="ppeEntity"
                  isLocked={hasInitialValue('ppeEntity')}
                  isHidden={isFieldHidden.ppeEntity}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ppeEntity',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlFormRadioGroup
                  name="ppePositionHolder"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_POSITION_HOLDER',
                  })}
                  options={[
                    {
                      label: TrlFormattedMessagePlain({
                        id: 'TRLT_UI-ONESELF',
                      }),
                      id: 'P',
                    },
                    {
                      label: TrlFormattedMessagePlain({
                        id: 'TRLT_UI-SOMEONE_ELSE',
                      }),
                      id: 'O',
                    },
                  ]}
                  isLocked={hasInitialValue('ppePositionHolder')}
                  isHidden={isFieldHidden.ppePositionHolder}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ppePositionHolder',
                    fieldProperty: 'isRequired',
                  })}
                  additionalFunc={(event) => {
                    if (event.target.value === 'P') {
                      if (!isFieldHidden.ppeOthersName) {
                        setFieldValue('ppeOthersName', '', false);
                      } else if (!isFieldHidden.ppePositionRelation) {
                        setFieldValue('ppePositionRelation', '', false);
                      }
                    }
                  }}
                />
                {values.ppePositionHolder === 'O' && (
                  <>
                    <TrlTextField
                      name="ppeOthersName"
                      type="text"
                      label={TrlFormattedMessagePlain({
                        id: 'TRLT_UI-PPE_OTHERS_NAME',
                      })}
                      placeholder={TrlFormattedMessagePlain({
                        id: 'TRLT_UI-PPE_OTHERS_NAME',
                      })}
                      id="ppeOthersName"
                      isLocked={hasInitialValue('ppeOthersName')}
                      isHidden={isFieldHidden.ppeOthersName}
                      isRequired={verifyFieldProperty({
                        fieldName: 'ppeOthersName',
                        fieldProperty: 'isRequired',
                      })}
                    />
                    <TrlTextField
                      name="ppePositionRelation"
                      type="text"
                      label={TrlFormattedMessagePlain({
                        id: 'TRLT_UI-PPE_POSITION_RELATION',
                      })}
                      placeholder={TrlFormattedMessagePlain({
                        id: 'TRLT_UI-PPE_POSITION_RELATION',
                      })}
                      id="ppePositionRelation"
                      isLocked={hasInitialValue('ppePositionRelation')}
                      isHidden={isFieldHidden.ppePositionRelation}
                      isRequired={verifyFieldProperty({
                        fieldName: 'ppePositionRelation',
                        fieldProperty: 'isRequired',
                      })}
                    />
                  </>
                )}
                <TrlTextField
                  name="ppeInstitution"
                  type="text"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_INSTITUTION',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_INSTITUTION',
                  })}
                  id="ppeInstitution"
                  isLocked={hasInitialValue('ppeInstitution')}
                  isHidden={isFieldHidden.ppeInstitution}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ppeInstitution',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlDatePicker
                  name="ppeAppointDate"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_APPOINTMENT_DATE',
                  })}
                  value={values.ppeAppointDate}
                  disableFuture
                  isLocked={hasInitialValue('ppeAppointDate')}
                  dateFormatType="date"
                  isHidden={isFieldHidden.ppeAppointDate}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ppeAppointDate',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlDatePicker
                  name="ppeExonDate"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_DISMISSAL_DATE',
                  })}
                  value={values.ppeExonDate}
                  disableFuture
                  isLocked={hasInitialValue('ppeExonDate')}
                  dateFormatType="date"
                  isHidden={isFieldHidden.ppeExonDate}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ppeExonDate',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlFormRadioGroup
                  name="ppeLeadershipMember"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_RELATION_QUESTION',
                  })}
                  options={[
                    { label: yesLabel, id: 'Y' },
                    { label: noLabel, id: 'N' },
                  ]}
                  isLocked={hasInitialValue('ppeLeadershipMember')}
                  isHidden={isFieldHidden.ppeLeadershipMember}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ppeLeadershipMember',
                    fieldProperty: 'isRequired',
                  })}
                  additionalFunc={(event) => {
                    if (
                      event.target.value === 'N' &&
                      !isFieldHidden.ppeLeadershipMemberRole
                    )
                      setFieldValue('ppeLeadershipMemberRole', '', false);
                  }}
                />
                {values.ppeLeadershipMember === 'Y' && (
                  <TrlTextField
                    name="ppeLeadershipMemberRole"
                    type="text"
                    label={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-PPE_RELATION_RESPONSE',
                    })}
                    placeholder={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-PPE_RELATION_RESPONSE',
                    })}
                    id="ppeLeadershipMemberRole"
                    isLocked={hasInitialValue('ppeLeadershipMemberRole')}
                    isHidden={isFieldHidden.ppeLeadershipMemberRole}
                    isRequired={verifyFieldProperty({
                      fieldName: 'ppeLeadershipMemberRole',
                      fieldProperty: 'isRequired',
                    })}
                  />
                )}
                <TrlFormRadioGroup
                  name="ppeReligiousOrgOfficer"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-PPE_RELIGIOUS',
                  })}
                  options={[
                    { label: yesLabel, id: 'Y' },
                    { label: noLabel, id: 'N' },
                  ]}
                  isLocked={hasInitialValue('ppeReligiousOrgOfficer')}
                  isHidden={isFieldHidden.ppeReligiousOrgOfficer}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ppeReligiousOrgOfficer',
                    fieldProperty: 'isRequired',
                  })}
                  additionalFunc={(event) => {
                    if (
                      event.target.value === 'N' &&
                      !isFieldHidden.ppeReligiousOrgOfficerRole
                    )
                      setFieldValue('ppeReligiousOrgOfficerRole', '', false);
                  }}
                />
                {values.ppeReligiousOrgOfficer === 'Y' && (
                  <TrlTextField
                    name="ppeReligiousOrgOfficerRole"
                    type="text"
                    label={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-PPE_RELIGIOUS_RESPONSE',
                    })}
                    placeholder={TrlFormattedMessagePlain({
                      id: 'TRLT_UI-PPE_RELIGIOUS_RESPONSE',
                    })}
                    id="ppeReligiousOrgOfficerRole"
                    isLocked={hasInitialValue('ppeReligiousOrgOfficerRole')}
                    isHidden={isFieldHidden.ppeReligiousOrgOfficerRole}
                    isRequired={verifyFieldProperty({
                      fieldName: 'ppeReligiousOrgOfficerRole',
                      fieldProperty: 'isRequired',
                    })}
                  />
                )}
              </>
            )}
            <TrlFormRadioGroup
              name="ppeFamily"
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-FAMILIAR_PPE' })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('ppeFamily')}
              isHidden={verifyFieldProperty({
                fieldName: 'ppeFamily',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'ppeFamily',
                fieldProperty: 'isRequired',
              })}
              additionalFunc={(event) => {
                if (event.target.value === 'N' && !isFieldHidden.ppeKinshipDeg)
                  setFieldValue('ppeKinshipDeg', '', false);
              }}
            />
            {values.ppeFamily === 'Y' && (
              <TrlTextField
                name="ppeKinshipDeg"
                type="text"
                label={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-KINSHIP_DEGREE',
                })}
                placeholder={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-KINSHIP_DEGREE',
                })}
                id="ppeKinshipDeg"
                isLocked={hasInitialValue('ppeKinshipDeg')}
                isHidden={isFieldHidden.ppeKinshipDeg}
                isRequired={verifyFieldProperty({
                  fieldName: 'ppeKinshipDeg',
                  fieldProperty: 'isRequired',
                })}
              />
            )}
            <TrlFormRadioGroup
              name="ppeRelationship"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-PPE_RELATIONSHIP',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('ppeRelationship')}
              isHidden={verifyFieldProperty({
                fieldName: 'ppeRelationship',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'ppeRelationship',
                fieldProperty: 'isRequired',
              })}
            />
          </Box>
        </>
      )}

      {isAureaActiveClient && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-BUSINESS_PURPOSE',
            })}
          />
          <Box className="flexWrap auto-width">
            <TrlTextField
              name="businessPurpose"
              type="text"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-BUSINESS_PURPOSE',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-BUSINESS_PURPOSE',
              })}
              id="businessPurpose"
              isLocked={hasInitialValue('businessPurpose')}
              isHidden={verifyFieldProperty({
                fieldName: 'businessPurpose',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'businessPurpose',
                fieldProperty: 'isRequired',
              })}
            />
          </Box>
        </>
      )}

      {(isAureaActiveClient ||
        isBfacmActiveClient ||
        isStinvestActiveClient) && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-INCOME_SOURCE_LEVEL',
            })}
          />
          <Box className="flexWrap">
            <TrlFormDropdown
              name="incomeSource"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-INCOME_SOURCE_OPTIONS',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-INCOME_SOURCE_OPTIONS',
              })}
              isLocked={hasInitialValue('incomeSource')}
              options={getFieldOptions('incomeSource')}
              isMultipleOption={isStinvestActiveClient}
              isHidden={verifyFieldProperty({
                fieldName: 'incomeSource',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'incomeSource',
                fieldProperty: 'isRequired',
              })}
              onChangeValue={(event) => {
                if (
                  isStinvestActiveClient &&
                  !event.target.value.includes('OUTRO') &&
                  !isFieldHidden.incomeSourceOthers
                )
                  setFieldValue('incomeSourceOthers', '', false);
              }}
            />
            {isStinvestActiveClient &&
            values.incomeSource?.includes('OUTRO') ? (
              <TrlTextField
                name="incomeSourceOthers"
                type="text"
                label={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-INCOME_SOURCE_OTHERS',
                })}
                placeholder={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-INCOME_SOURCE_OTHERS',
                })}
                id="incomeSourceOthers"
                isLocked={hasInitialValue('incomeSourceOthers')}
                isHidden={verifyFieldProperty({
                  fieldName: 'incomeSourceOthers',
                  fieldProperty: 'isHidden',
                })}
                isRequired={verifyFieldProperty({
                  fieldName: 'incomeSourceOthers',
                  fieldProperty: 'isRequired',
                })}
              />
            ) : (
              !isStinvestActiveClient && (
                <TrlFormRadioGroup
                  name="incomeSourceOthers"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-INCOME_SOURCE_OTHERS',
                  })}
                  options={[
                    { label: yesLabel, id: 'Y' },
                    { label: noLabel, id: 'N' },
                  ]}
                  isLocked={hasInitialValue('incomeSourceOthers')}
                  isHidden={verifyFieldProperty({
                    fieldName: 'incomeSourceOthers',
                    fieldProperty: 'isHidden',
                  })}
                  isRequired={verifyFieldProperty({
                    fieldName: 'incomeSourceOthers',
                    fieldProperty: 'isRequired',
                  })}
                  additionalFunc={(event) => {
                    if (
                      event.target.value === 'N' &&
                      !isFieldHidden.incomeSourceDescr
                    )
                      setFieldValue('incomeSourceDescr', '', false);
                  }}
                />
              )
            )}
            {(!isBfacmActiveClient ||
              (isBfacmActiveClient && values.incomeSourceOthers === 'Y')) && (
              <TrlTextField
                name="incomeSourceDescr"
                type="text"
                label={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-INCOME_SOURCE_DESCRIPTION',
                })}
                placeholder={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-INCOME_SOURCE_DESCRIPTION',
                })}
                id="incomeSourceDescr"
                isLocked={hasInitialValue('incomeSourceDescr')}
                isHidden={isFieldHidden.incomeSourceDescr}
                isRequired={
                  verifyFieldProperty({
                    fieldName: 'incomeSourceDescr',
                    fieldProperty: 'isRequired',
                  }) &&
                  (isAureaActiveClient
                    ? values.incomeSource === 'ORR_OUTO'
                    : true)
                }
              />
            )}
            <TrlTextField
              name="incomeLevel"
              type="text"
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-INCOME_LEVEL' })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-INCOME_LEVEL',
              })}
              id="incomeLevel"
              isLocked={hasInitialValue('incomeLevel')}
              isHidden={verifyFieldProperty({
                fieldName: 'incomeLevel',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'incomeLevel',
                fieldProperty: 'isRequired',
              })}
              additionalFunc={(event) => {
                if (isStinvestActiveClient) {
                  const fieldValue = event.target.value;
                  const valueInFull = convertValueToWords({
                    value: fieldValue,
                    currencyCode: values.incomeCurrency,
                  });

                  setFieldValue('incomeLevelInFull', valueInFull, false);
                  setFieldValue(
                    'incomeLevelInFullDestination',
                    valueInFull,
                    false,
                  );
                  setFieldValue('incomeLevelDestination', fieldValue, false);
                }
              }}
              inputMode="numeric"
            />
            <TrlFormDropdown
              name="incomeCurrency"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-CURRENCY',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-CURRENCY',
              })}
              isLocked={hasInitialValue('incomeCurrency')}
              options={getFieldOptions('incomeCurrency')}
              isHidden={verifyFieldProperty({
                fieldName: 'incomeCurrency',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'incomeCurrency',
                fieldProperty: 'isRequired',
              })}
              onChangeValue={(event) => {
                if (isStinvestActiveClient) {
                  const fieldValue = event.target.value;
                  const valueInFull = convertValueToWords({
                    value: values.incomeLevel,
                    currencyCode: fieldValue,
                  });

                  setFieldValue('incomeLevelInFull', valueInFull, false);
                  setFieldValue(
                    'incomeLevelInFullDestination',
                    valueInFull,
                    false,
                  );
                  setFieldValue('incomeCurrencyDestination', fieldValue, false);
                }
              }}
            />
            <TrlTextField
              name="incomeLevelInFull"
              type="text"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-INCOME_LEVEL_WORDS',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-INCOME_LEVEL_WORDS',
              })}
              id="incomeLevelInFull"
              isLocked
              isHidden={verifyFieldProperty({
                fieldName: 'incomeLevelInFull',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'incomeLevelInFull',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormDropdown
              name="incomeValues"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-INCOME_VALUES',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-INCOME_VALUES',
              })}
              isLocked={hasInitialValue('incomeValues')}
              options={getFieldOptions('incomeValues')}
              isHidden={verifyFieldProperty({
                fieldName: 'incomeValues',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'incomeValues',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlTextField
              name="relationAssetsOthers"
              type="text"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-INCOME_RELATIONS',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-INCOME_RELATIONS',
              })}
              id="relationAssetsOthers"
              isLocked={hasInitialValue('relationAssetsOthers')}
              isHidden={verifyFieldProperty({
                fieldName: 'relationAssetsOthers',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'relationAssetsOthers',
                fieldProperty: 'isRequired',
              })}
            />
          </Box>
        </>
      )}

      {isBfacmActiveClient && (
        <>
          <TrlFormSectionSeparator title="Posse de Bens" />
          <Box className="flexWrap">
            <TrlFormDropdown
              name="mainDwelling"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-MAIN_DWELLING',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-MAIN_DWELLING',
              })}
              isLocked={hasInitialValue('mainDwelling')}
              options={getFieldOptions('mainDwelling')}
              isHidden={verifyFieldProperty({
                fieldName: 'mainDwelling',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'mainDwelling',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormDropdown
              name="car"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-CAR',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-CAR',
              })}
              isLocked={hasInitialValue('car')}
              options={getFieldOptions('car')}
              isHidden={verifyFieldProperty({
                fieldName: 'car',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'car',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormDropdown
              name="realEstateTotalValue"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-REAL_ESTATE_TOTAL_VALUE',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-REAL_ESTATE_TOTAL_VALUE',
              })}
              isLocked={hasInitialValue('realEstateTotalValue')}
              options={getFieldOptions('realEstateTotalValue')}
              isHidden={verifyFieldProperty({
                fieldName: 'realEstateTotalValue',
                fieldProperty: 'isHidden',
              })}
              isRequired={
                verifyFieldProperty({
                  fieldName: 'realEstateTotalValue',
                  fieldProperty: 'isRequired',
                }) && values.mainDwelling === 'HBP_PROP'
              }
            />
            <TrlFormDropdown
              name="movableAssetsTotalValue"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-MOVABLE_ASSETS_TOTAL_VALUE',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-MOVABLE_ASSETS_TOTAL_VALUE',
              })}
              isLocked={hasInitialValue('movableAssetsTotalValue')}
              options={getFieldOptions('movableAssetsTotalValue')}
              isHidden={verifyFieldProperty({
                fieldName: 'movableAssetsTotalValue',
                fieldProperty: 'isHidden',
              })}
              isRequired={
                verifyFieldProperty({
                  fieldName: 'movableAssetsTotalValue',
                  fieldProperty: 'isRequired',
                }) && values.car === 'AUT_PROP'
              }
            />
          </Box>
        </>
      )}

      {(isBfacmActiveClient || isStinvestActiveClient) && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({ id: 'TRLT_UI-FATCA' })}
          />
          <Box className="flexWrap">
            <TrlFormRadioGroup
              name="americanNationality"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-AMERICAN_NATIONALITY',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('americanNationality')}
              isHidden={verifyFieldProperty({
                fieldName: 'americanNationality',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'americanNationality',
                fieldProperty: 'isRequired',
              })}
              additionalFunc={(event) => {
                if (event.target.value === 'N' && !isFieldHidden.usTaxId)
                  setFieldValue('usTaxId', '', false);
              }}
            />
            {values.americanNationality === 'Y' && (
              <TrlTextField
                name="usTaxId"
                type="text"
                label={TrlFormattedMessagePlain({ id: 'TRLT_UI-US_TAX_ID' })}
                placeholder={TrlFormattedMessagePlain({
                  id: 'TRLT_UI-US_TAX_ID',
                })}
                id="usTaxId"
                isLocked={hasInitialValue('usTaxId')}
                isHidden={isFieldHidden.usTaxId}
                isRequired={verifyFieldProperty({
                  fieldName: 'usTaxId',
                  fieldProperty: 'isRequired',
                })}
                inputProps={{
                  maxLength: 10,
                }}
              />
            )}
          </Box>
        </>
      )}

      {(isAureaActiveClient || isHemeraActiveClient) && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-SERVICES_PROVIDED',
            })}
          />
          <Box className="flexWrap">
            <TrlFormRadioGroup
              name="financialInstr"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-FINANCIAL_INSTRUMENTS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('financialInstr')}
              isHidden={verifyFieldProperty({
                fieldName: 'financialInstr',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'financialInstr',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="marketOrders"
              label={TrlFormattedMessagePlain({ id: 'TRLT_UI-MARKET_ORDERS' })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('marketOrders')}
              isHidden={verifyFieldProperty({
                fieldName: 'marketOrders',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'marketOrders',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlTextField
              name="businessSource"
              type="text"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-BUSINESS_NATURE',
              })}
              placeholder={TrlFormattedMessagePlain({
                id: 'TRLT_UI-BUSINESS_NATURE',
              })}
              id="businessSource"
              isLocked={hasInitialValue('businessSource')}
              isHidden={verifyFieldProperty({
                fieldName: 'businessSource',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'businessSource',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="regDepFinantialInstr"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-REG_CUSTODY_FIN_INSTRUMENTS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('regDepFinantialInstr')}
              isHidden={verifyFieldProperty({
                fieldName: 'regDepFinantialInstr',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'regDepFinantialInstr',
                fieldProperty: 'isRequired',
              })}
              additionalFunc={(event) => {
                setFieldValue('recepExecOrders', event.target.value, false);
              }}
            />
          </Box>
        </>
      )}

      {isHemeraActiveClient && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-SEC_DERIVAT_INSTRUMENTS',
            })}
          />
          <Box className="flexWrap">
            <TrlFormRadioGroup
              name="stocks"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-STOCKS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('stocks')}
              isHidden={verifyFieldProperty({
                fieldName: 'stocks',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'stocks',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="corporateObligations"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-CORPORATE_OBLIGATIONS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('corporateObligations')}
              isHidden={verifyFieldProperty({
                fieldName: 'corporateObligations',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'corporateObligations',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="treasuryBonds"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-TREASURY_BONDS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('treasuryBonds')}
              isHidden={verifyFieldProperty({
                fieldName: 'treasuryBonds',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'treasuryBonds',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="treasuryBills"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-TREASURY_BILLS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('treasuryBills')}
              isHidden={verifyFieldProperty({
                fieldName: 'treasuryBills',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'treasuryBills',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="collectiveInvestUnits"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-COLLECTIVE_INVESTUNITS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('collectiveInvestUnits')}
              isHidden={verifyFieldProperty({
                fieldName: 'collectiveInvestUnits',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'collectiveInvestUnits',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="depositCertificates"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-DEPOSIT_CERTIFICATES',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('depositCertificates')}
              isHidden={verifyFieldProperty({
                fieldName: 'depositCertificates',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'depositCertificates',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="derivativeInstruments"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-DERIVATIVE_INSTRUMENTS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('derivativeInstruments')}
              isHidden={verifyFieldProperty({
                fieldName: 'derivativeInstruments',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'derivativeInstruments',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="structuredProducts"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-STRUCTURED_PRODUCTS',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('structuredProducts')}
              isHidden={verifyFieldProperty({
                fieldName: 'structuredProducts',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'structuredProducts',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="commercialPaper"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-COMMERCIAL_PAPER',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('commercialPaper')}
              isHidden={verifyFieldProperty({
                fieldName: 'commercialPaper',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'commercialPaper',
                fieldProperty: 'isRequired',
              })}
            />
          </Box>
        </>
      )}

      {isHemeraActiveClient && (
        <>
          <TrlFormSectionSeparator
            title={TrlFormattedMessagePlain({
              id: 'TRLT_UI-ORDER_TRANS_METHOD',
            })}
          />
          <Box className="flexWrap">
            <TrlFormRadioGroup
              name="ordTransInPerson"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-IN_PERSON',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('ordTransInPerson')}
              isHidden={verifyFieldProperty({
                fieldName: 'ordTransInPerson',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'ordTransInPerson',
                fieldProperty: 'isRequired',
              })}
            />
            <TrlFormRadioGroup
              name="ordTransHasEmail"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-EMAIL_ONBOARDING',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('ordTransHasEmail')}
              isHidden={verifyFieldProperty({
                fieldName: 'ordTransHasEmail',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'ordTransHasEmail',
                fieldProperty: 'isRequired',
              })}
              additionalFunc={(event) => {
                if (event.target.value === 'N') {
                  if (!isFieldHidden.ordTransEmail)
                    setFieldValue('ordTransEmail', '', false);
                  if (!isFieldHidden.ordTransEmail2)
                    setFieldValue('ordTransEmail2', '', false);
                }
              }}
            />
            {values.ordTransHasEmail === 'Y' && (
              <>
                <TrlTextField
                  name="ordTransEmail"
                  type="email"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-EMAIL_ADDRESS',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-EMAIL_ADDRESS',
                  })}
                  id="ordTransEmail"
                  isLocked={hasInitialValue('ordTransEmail')}
                  isHidden={isFieldHidden.ordTransEmail}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ordTransEmail',
                    fieldProperty: 'isRequired',
                  })}
                />
                <TrlTextField
                  name="ordTransEmail2"
                  type="email"
                  label={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-ALTERNATIVE_EMAIL_ADDRESS',
                  })}
                  placeholder={TrlFormattedMessagePlain({
                    id: 'TRLT_UI-ALTERNATIVE_EMAIL_ADDRESS',
                  })}
                  id="ordTransEmail2"
                  isLocked={hasInitialValue('ordTransEmail2')}
                  isHidden={isFieldHidden.ordTransEmail2}
                  isRequired={verifyFieldProperty({
                    fieldName: 'ordTransEmail2',
                    fieldProperty: 'isRequired',
                  })}
                />
              </>
            )}
            <TrlFormRadioGroup
              name="ordTransPhoneNum"
              label={TrlFormattedMessagePlain({
                id: 'TRLT_UI-PHONE_CONTACT',
              })}
              options={[
                { label: yesLabel, id: 'Y' },
                { label: noLabel, id: 'N' },
              ]}
              isLocked={hasInitialValue('ordTransPhoneNum')}
              isHidden={verifyFieldProperty({
                fieldName: 'ordTransPhoneNum',
                fieldProperty: 'isHidden',
              })}
              isRequired={verifyFieldProperty({
                fieldName: 'ordTransPhoneNum',
                fieldProperty: 'isRequired',
              })}
            />
          </Box>
        </>
      )}
    </>
  );
};
